import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { TUserTypes } from '../../../types/IPENSessionData';
import { IOrder } from '../../../types/Order';
import { ISigner } from '../../../types/Parties';
import { OrderStatus } from '../../constants/OrderStatus';
import {
  SignerType,
  bySignerType,
  isSignerOfType,
} from '../../constants/solexSignerTypes';
import { IIPENSessionState } from '../../redux-store/ipenSession/ipenSessionSlice';
import { isOrderTypeIPEN } from '../../utils/helpers';
import OrderTrackerRow from '../OrderTrackerRow';
import './_base.scss';

interface IProps {
  unlockParty?: (partyId: string) => void;
  data?: IOrder;
  ipenSession?: IIPENSessionState;
  unlockSession?: (partyId: string) => void;
  userType?: TUserTypes[];
  isCurrentUserEscrowOnOrder: boolean;
}

type EnrichedSigner = ISigner & {
  includeSequenceInLabel: boolean;
  signingDelegator?: ISigner;
};

function ConformXPartyInfo(props: IProps) {
  const {
    data,
    ipenSession,
    unlockParty,
    unlockSession,
    userType,
    isCurrentUserEscrowOnOrder,
  } = props;

  const goResetLogin = (partyId: string) => {
    if (unlockParty) {
      unlockParty(partyId);
    }
  };

  const goUnlockSession = (partyId: string) => {
    if (unlockSession) {
      unlockSession(partyId);
    }
  };

  const isCurrentSigner = (signerId?: string) =>
    ipenSession?.activeSignerIds.some((id) => signerId === id);

  const displayUnlock = () => {
    let isUnlocked = false;
    if (
      userType &&
      (userType.includes('global') ||
        (userType.includes('escrow') && isCurrentUserEscrowOnOrder)) &&
      (data?.Status == OrderStatus.OpenAllPreviewed ||
        data?.Status == OrderStatus.OpenLinksSent ||
        data?.Status == OrderStatus.OpenAllSigned)
    ) {
      isUnlocked = true;
    }
    return isUnlocked;
  };

  const getSigners = (): EnrichedSigner[] => {
    if (!data?.OrderDetails?.Signers) {
      return [];
    }

    const signers: EnrichedSigner[] = data.OrderDetails.Signers.filter(
      (signer) => !isSignerOfType(signer, SignerType.PowerOfAttorney)
    )
      .map((signer) => {
        const signersOfSameType = data.OrderDetails?.Signers?.filter(
          (dataSigner) =>
            dataSigner.SignerType?.toLowerCase() ===
            signer.SignerType?.toLowerCase()
        );
        return {
          ...signer,
          includeSequenceInLabel: signersOfSameType
            ? signersOfSameType.length > 1
            : false,
        };
      })
      .sort(bySignerType());

    data.OrderDetails.Signers.filter((signer) =>
      isSignerOfType(signer, SignerType.PowerOfAttorney)
    ).forEach((poa) => {
      const signingDelegator = signers.find(
        (signer) => signer.SigningDelegatePartyId === poa.Id
      );
      if (signingDelegator) {
        const indexAfterSigningDelegator =
          signers.indexOf(signingDelegator) + 1;
        signers.splice(indexAfterSigningDelegator, 0, {
          ...poa,
          includeSequenceInLabel: signingDelegator.includeSequenceInLabel,
          signingDelegator: signingDelegator,
        });
      }
    });

    return signers;
  };

  return (
    <div>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} size='small' aria-label='a dense table'>
          <TableHead>
            <TableRow>
              <TableCell>PARTY TYPE</TableCell>
              <TableCell>PARTY NAME</TableCell>
              <TableCell>PARTY EMAIL</TableCell>
              <TableCell>SIGN STATUS</TableCell>
              <TableCell style={{ textAlign: 'center' }}>ESIGN LINK</TableCell>
              {displayUnlock() && (
                <TableCell style={{ textAlign: 'center' }}>
                  RESET LOGIN
                </TableCell>
              )}
              {isOrderTypeIPEN(data) && ipenSession?.isActiveSession && (
                <TableCell style={{ textAlign: 'center' }}>
                  ACTIVATE SESSION
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {getSigners().map((signer, index) => {
              const events: any = signer?.SolexSigner?.SignerEvents
                ? signer?.SolexSigner?.SignerEvents
                : [];
              return (
                <OrderTrackerRow
                  key={index}
                  displayUnlock={displayUnlock()}
                  unlockParty={goResetLogin}
                  unlockSession={goUnlockSession}
                  party={signer}
                  signingDelegatorParty={signer.signingDelegator}
                  includeSequenceInLabel={signer.includeSequenceInLabel}
                  events={events}
                  isIPEN={isOrderTypeIPEN(data)}
                  isActiveSession={ipenSession?.isActiveSession}
                  orderStatus={data?.Status}
                  isActiveSigner={isCurrentSigner(signer.Id)}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default ConformXPartyInfo;
